import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, XCircle, Camera, RotateCcw, LogOut, Keyboard } from "lucide-react";

const CITY_LABEL = {
  sydney: "Sydney",
  melbourne: "Melbourne",
  adelaide: "Adelaide",
  london: "London",
  paris: "Paris",
};

// Strip our `/api/admin/scan/<ref>` URL or other prefixes if the QR contains a
// URL rather than the bare reference. We always operate on the trailing token,
// also extracting a `?ref=` query param if present.
function extractReference(raw) {
  if (!raw) return "";
  const s = String(raw).trim();
  // Try ?ref=... or &ref=...
  const m = s.match(/[?&]ref=([^&\s#]+)/i);
  if (m) return decodeURIComponent(m[1]).toUpperCase();
  // Fallback: take the last path segment (handles URLs and bare refs)
  const tail = s.split(/[/?#]/).filter(Boolean).pop() || "";
  return tail.toUpperCase();
}

const STATUS_STYLE = {
  ok:         { Icon: CheckCircle2,  color: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-400/40", label: "Admitted"  },
  valid:      { Icon: CheckCircle2,  color: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-400/40", label: "Valid"     },
  duplicate:  { Icon: AlertTriangle, color: "text-amber-300",   bg: "bg-amber-500/10",   border: "border-amber-400/50",   label: "Already in" },
  checked_in: { Icon: AlertTriangle, color: "text-amber-300",   bg: "bg-amber-500/10",   border: "border-amber-400/50",   label: "Already in" },
  invalid:    { Icon: XCircle,       color: "text-rose-300",    bg: "bg-rose-500/10",    border: "border-rose-400/40",    label: "Invalid"    },
};

function playFeedback(status) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    const tone =
      status === "ok" ? 880 :
      status === "duplicate" || status === "checked_in" ? 440 :
      220;
    o.frequency.value = tone;
    o.connect(g);
    g.connect(ctx.destination);
    g.gain.setValueAtTime(0.15, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    o.start();
    o.stop(ctx.currentTime + 0.45);
  } catch { /* ignore */ }
}

export default function AdminScan() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Magic-link admin key — passed via URL fragment so it never hits the server
  // logs. Format: `/admin/scan#k=<key>` or `/admin/scan?ref=XXX#k=<key>`.
  // If present, we persist to localStorage AND sign in immediately so the user
  // only ever needs the bookmark once per device.
  // We read the fragment synchronously during render init so the initial
  // useState below picks the key up too.
  const magicKey = (() => {
    if (typeof window === "undefined") return "";
    const m = (window.location.hash || "").match(/[#&]k=([^&]+)/);
    return m ? decodeURIComponent(m[1]) : "";
  })();
  if (magicKey) {
    try { localStorage.setItem("metamorph_admin_key", magicKey); } catch { /* ignore */ }
  }

  // Strip the key fragment from the URL once mounted — so it isn't left in
  // history or accidentally shared via a screenshot.
  useEffect(() => {
    if (!magicKey || typeof window === "undefined") return;
    const cleanHash = (window.location.hash || "").replace(/[#&]k=[^&]+/, "").replace(/^#/, "");
    window.history.replaceState(
      null,
      "",
      window.location.pathname + window.location.search + (cleanHash ? "#" + cleanHash : "")
    );
  }, [magicKey]);

  const [adminKey, setAdminKey] = useState(() => {
    try { return localStorage.getItem("metamorph_admin_key") || ""; } catch { return ""; }
  });
  // Optimistic: if a key is stored we render the scan UI immediately; any
  // /admin/scan call will surface 401 and kick the user back to sign-in.
  const [authed, setAuthed] = useState(() => {
    try { return !!localStorage.getItem("metamorph_admin_key"); } catch { return false; }
  });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [manualRef, setManualRef] = useState("");
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  const scannerRef = useRef(null);            // Html5Qrcode instance
  const lastScanRef = useRef({ ref: "", at: 0 }); // de-dupe rapid re-scans
  const checkInRef = useRef(null);            // stable handle for camera callback

  // ── Auth ───────────────────────────────────────────────────────────────
  const onSignIn = (e) => {
    e.preventDefault();
    const v = adminKey.trim();
    if (!v) { toast.error("Enter the admin key."); return; }
    try { localStorage.setItem("metamorph_admin_key", v); } catch { /* ignore */ }
    setAdminKey(v);
    setAuthed(true);
  };

  const onSignOut = () => {
    stopCamera();
    try { localStorage.removeItem("metamorph_admin_key"); } catch { /* ignore */ }
    setAdminKey("");
    setAuthed(false);
    setResult(null);
  };

  // ── Camera lifecycle ───────────────────────────────────────────────────
  function stopCamera() {
    const inst = scannerRef.current;
    scannerRef.current = null;
    setCameraActive(false);
    if (!inst) return;
    Promise.resolve()
      .then(() => inst.stop().catch(() => {}))
      .then(() => inst.clear().catch(() => {}));
  }

  async function startCamera() {
    // Yield to microtask so any synchronous setState below doesn't fire
    // during the calling useEffect (which the react-hooks lint flags).
    await Promise.resolve();
    setCameraError(null);
    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      const containerId = "qr-reader";
      const el = document.getElementById(containerId);
      if (!el) return;
      const instance = new Html5Qrcode(containerId, { verbose: false });
      scannerRef.current = instance;
      await instance.start(
        { facingMode: "environment" },
        { fps: 12, qrbox: { width: 260, height: 260 }, aspectRatio: 1.0 },
        (decodedText) => { checkInRef.current?.(decodedText); },
        () => { /* per-frame failure — ignore */ }
      );
      setCameraActive(true);
    } catch (err) {
      setCameraError(err?.message || "Camera unavailable. Use manual entry below.");
      setCameraActive(false);
    }
  }

  // ── Scan handler ───────────────────────────────────────────────────────
  async function checkIn(rawText) {
    const ref = extractReference(rawText);
    if (!ref) return;
    const now = Date.now();
    if (ref === lastScanRef.current.ref && now - lastScanRef.current.at < 2500) return;
    lastScanRef.current = { ref, at: now };

    setBusy(true);
    try {
      const { data } = await api.post(`/admin/scan/${encodeURIComponent(ref)}`, null, {
        headers: { "X-Admin-Key": adminKey },
      });
      setResult(data);
      playFeedback(data.status);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Sign in again.");
        try { localStorage.removeItem("metamorph_admin_key"); } catch { /* ignore */ }
        stopCamera();
        setAuthed(false);
      } else {
        toast.error("Scan failed. Try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  // Bind the latest `checkIn` reference into a ref so the long-lived camera
  // callback always sees the freshest adminKey/state without re-instantiating
  // the scanner on every render.
  checkInRef.current = checkIn;

  // Mount: start camera if authed. Unmount: tear down.
  // Deferred via setTimeout so the lint rule sees the setState calls inside
  // startCamera as happening outside the effect's synchronous execution.
  useEffect(() => {
    if (!authed) return undefined;
    const t = setTimeout(() => { startCamera(); }, 0);
    return () => {
      clearTimeout(t);
      stopCamera();
    };
  }, [authed]);

  // If the page is opened from a QR-encoded URL like `/admin/scan?ref=MM-SYD-XXX`,
  // auto-check-in once authed. Pop the query param after so a refresh doesn't
  // re-trigger the same scan.
  useEffect(() => {
    if (!authed) return;
    const refFromUrl = searchParams.get("ref");
    if (!refFromUrl) return;
    setSearchParams({}, { replace: true });
    setTimeout(() => { checkIn(refFromUrl); }, 0);
  }, [authed, searchParams, setSearchParams]);

  const onManualSubmit = (e) => {
    e.preventDefault();
    if (!manualRef.trim()) return;
    checkIn(manualRef.trim());
    setManualRef("");
  };

  // ── Render ─────────────────────────────────────────────────────────────
  if (!authed) {
    return (
      <main className="min-h-screen bg-black text-white flex flex-col px-6 py-10" data-testid="scan-gate">
        <header className="mb-12">
          <Link to="/" className="vintage-link text-xs sm:text-sm uppercase" data-testid="scan-back">← Back</Link>
        </header>
        <div className="flex-1 flex items-center justify-center">
          <form onSubmit={onSignIn} className="w-full max-w-sm">
            <h1 className="vintage-text text-4xl sm:text-5xl mb-2 text-center" data-testid="scan-title">Door scan</h1>
            <p className="vintage-link text-[10px] uppercase tracking-[0.22em] text-center opacity-70 mb-10">
              Admin only · live ticket check-in
            </p>
            <input
              type="password"
              value={adminKey}
              onChange={(e) => setAdminKey(e.target.value)}
              placeholder="Admin key"
              className="line-input"
              autoFocus
              data-testid="scan-key-input"
            />
            <button type="submit" className="btn-line btn-filled w-full justify-center mt-6" data-testid="scan-signin">
              Enter
            </button>
          </form>
        </div>
      </main>
    );
  }

  const status = result?.status;
  const style = status ? STATUS_STYLE[status] : null;

  return (
    <main className="min-h-screen bg-black text-white px-5 sm:px-10 py-6" data-testid="scan-dashboard">
      <header className="flex items-center justify-between mb-6">
        <Link to="/admin/tickets" className="vintage-link text-xs uppercase" data-testid="scan-to-tickets">
          ← Tickets
        </Link>
        <h1 className="vintage-text text-2xl sm:text-3xl" data-testid="scan-heading">Door · Scan</h1>
        <button onClick={onSignOut} className="vintage-link text-xs uppercase flex items-center gap-2 opacity-70" data-testid="scan-signout">
          <LogOut className="w-3 h-3" strokeWidth={1.2} /> Sign out
        </button>
      </header>

      <section className="grid lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
        {/* Camera column */}
        <div>
          <div className="relative aspect-square w-full max-w-md mx-auto border border-white/15 bg-black/60 overflow-hidden" data-testid="scan-camera">
            <div id="qr-reader" className="w-full h-full" />
            {!cameraActive && !cameraError && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/55">
                <Camera className="w-8 h-8" strokeWidth={1} />
                <span className="vintage-link text-[10px] uppercase tracking-[0.22em]">Starting camera…</span>
              </div>
            )}
            {cameraError && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center text-white/70">
                <AlertTriangle className="w-6 h-6 text-amber-300" strokeWidth={1.2} />
                <p className="text-xs leading-relaxed">{cameraError}</p>
                <button onClick={startCamera} className="vintage-link text-[10px] uppercase tracking-[0.22em] mt-2" data-testid="scan-camera-retry">
                  <RotateCcw className="w-3 h-3 inline mr-1.5" strokeWidth={1.2} />Try again
                </button>
              </div>
            )}
          </div>

          <form onSubmit={onManualSubmit} className="mt-5 max-w-md mx-auto" data-testid="scan-manual-form">
            <label className="vintage-link text-[10px] uppercase tracking-[0.22em] opacity-70 flex items-center gap-2 mb-2">
              <Keyboard className="w-3 h-3" strokeWidth={1.2} /> Manual entry
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={manualRef}
                onChange={(e) => setManualRef(e.target.value.toUpperCase())}
                placeholder="MM-SYD-XXXXX"
                className="line-input flex-1 font-mono tracking-[0.16em]"
                data-testid="scan-manual-input"
              />
              <button type="submit" disabled={busy} className="btn-line whitespace-nowrap" data-testid="scan-manual-submit">
                Check in
              </button>
            </div>
          </form>
        </div>

        {/* Result column */}
        <div>
          {!result ? (
            <div className="border border-white/10 p-8 text-center text-white/45 min-h-[280px] flex items-center justify-center" data-testid="scan-empty">
              <div>
                <p className="vintage-link text-[10px] uppercase tracking-[0.22em] mb-3">Awaiting scan</p>
                <p className="text-sm">Point the camera at a Metamorph ticket QR code.</p>
              </div>
            </div>
          ) : (
            <div className={`border ${style?.border} ${style?.bg} backdrop-blur-sm p-8`} data-testid={`scan-result-${status}`}>
              <div className={`flex items-center gap-3 ${style?.color} mb-6`}>
                {style?.Icon && <style.Icon className="w-9 h-9" strokeWidth={1.2} />}
                <h2 className="vintage-text text-3xl sm:text-4xl" data-testid="scan-result-label">{style?.label}</h2>
              </div>

              {status !== "invalid" ? (
                <dl className="grid grid-cols-3 gap-y-3 gap-x-4 text-sm m-0">
                  <dt className="vintage-link text-[10px] uppercase tracking-[0.22em] opacity-65 col-span-1 m-0">Name</dt>
                  <dd className="col-span-2 m-0 text-white" data-testid="scan-result-name">{result.customer_name || "—"}</dd>

                  <dt className="vintage-link text-[10px] uppercase tracking-[0.22em] opacity-65 col-span-1 m-0">City</dt>
                  <dd className="col-span-2 m-0 text-white" data-testid="scan-result-city">
                    {CITY_LABEL[result.city] || result.city || "—"}
                  </dd>

                  <dt className="vintage-link text-[10px] uppercase tracking-[0.22em] opacity-65 col-span-1 m-0">Qty</dt>
                  <dd className="col-span-2 m-0 tabular-nums text-xl text-white" data-testid="scan-result-qty">× {result.quantity}</dd>

                  <dt className="vintage-link text-[10px] uppercase tracking-[0.22em] opacity-65 col-span-1 m-0">Ref</dt>
                  <dd className="col-span-2 m-0 font-mono text-xs text-white/90" data-testid="scan-result-ref">{result.reference}</dd>

                  {result.check_in?.checked_in_at && (
                    <>
                      <dt className="vintage-link text-[10px] uppercase tracking-[0.22em] opacity-65 col-span-1 m-0">Scanned</dt>
                      <dd className="col-span-2 m-0 text-xs text-white/75" data-testid="scan-result-time">
                        {new Date(result.check_in.checked_in_at).toLocaleString()}
                      </dd>
                    </>
                  )}
                </dl>
              ) : (
                <p className="text-base" data-testid="scan-result-invalid-msg">
                  No ticket found for reference <span className="font-mono">{result.reference}</span>.
                </p>
              )}

              <button
                type="button"
                onClick={() => setResult(null)}
                className="vintage-link text-[10px] uppercase tracking-[0.22em] mt-8 inline-flex items-center gap-2"
                data-testid="scan-result-dismiss"
              >
                <RotateCcw className="w-3 h-3" strokeWidth={1.2} /> Next scan
              </button>
            </div>
          )}
        </div>
      </section>

      {busy && (
        <div className="fixed inset-0 pointer-events-none flex items-center justify-center" data-testid="scan-busy">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/55 animate-pulse">Checking…</div>
        </div>
      )}
    </main>
  );
}
